import { useCallback, useEffect, useState } from 'react';
import type { Api, SuDashboard } from '../../api';
import { fmtDateTime } from '../../admin/format';
import { ORG_STATUS_LABEL, SU_STAT_TILES } from './labels';

/**
 * 수퍼유저 대시보드(§14.19.1) — 전체 현황 숫자 + 조직별 표.
 * 조직 이름을 누르면 그 조직으로 들어간다(`onEnterOrg` — 조직 관리자 화면).
 */
export function SuDashboardView({ api, onEnterOrg }: { api: Api; onEnterOrg: (orgId: string) => void }) {
  const [data, setData] = useState<SuDashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await api.suDashboard());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="view">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>대시보드</h2>
        <button className="ghost" onClick={() => void refresh()} disabled={loading}>
          {loading ? '불러오는 중…' : '새로고침'}
        </button>
      </div>
      {error && <div className="hint" style={{ color: 'var(--err)', marginBottom: 12 }}>{error}</div>}

      {data && (
        <>
          <div className="su-stats">
            {SU_STAT_TILES.map((t) => (
              <div key={t.label} className="su-stat">
                <div className="su-stat-value">{t.value(data.totals)}</div>
                <div className="su-stat-label">{t.label}</div>
              </div>
            ))}
          </div>

          <h3 style={{ margin: '20px 0 8px' }}>조직별 현황</h3>
          {data.orgs.length === 0 ? (
            <p className="hint">조직이 없습니다. [조직] 메뉴에서 만드세요.</p>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>조직</th>
                    <th>상태</th>
                    <th className="num">사용자</th>
                    <th className="num">관리자</th>
                    <th className="num">기기(온라인)</th>
                    <th className="num">세션 7일</th>
                    <th>최근 세션</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orgs.map((o) => (
                    <tr key={o.orgId}>
                      <td>
                        <button className="linklike" onClick={() => onEnterOrg(o.orgId)} title="이 조직으로 들어가기">
                          {o.orgName}
                        </button>
                      </td>
                      <td>
                        <span className={`badge ${o.status === 'active' ? '' : 'err'}`}>
                          {ORG_STATUS_LABEL[o.status] ?? o.status}
                        </span>
                      </td>
                      <td className="num">{o.users}</td>
                      <td className="num">{o.admins}</td>
                      <td className="num">
                        {o.devices} ({o.onlineDevices})
                      </td>
                      <td className="num">{o.sessions7d}</td>
                      <td className="nowrap">{fmtDateTime(o.lastSessionAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
